<template>
  <div class="overflow-hidden" :style="containerStyle">
    <div class="relative" :style="imageContainerStyle">
      <!-- Visible image - src only changes after new image is preloaded -->
      <img
        :src="currentUrl"
        draggable="false"
        class="object-fill block h-full w-full absolute pointer-events-none"
      />
    </div>
  </div>
</template>

<script setup>
  const props = defineProps({
    element: Object,
    surface: Object,
    contentOverride: {
      type: Object,
      default: null
    },
    renderScale: {
      type: Number,
      default: null
    }
  });

  const surfaceEditorUtils = useSurfaceEditorUtils();

  const image = computed(() => props.contentOverride || props.element.content);

  const {imageUrl: displayImageUrl} = useSurfaceEditorImageRequest({
    image,
    renderScale: computed(() => props.renderScale ?? 1)
  });

  // Track the base image URL to detect when image is replaced entirely
  const baseImageUrl = computed(() => image.value.url);

  // Current visible image URL - only updated after preload completes (for same image)
  // or immediately when the base image changes (different image)
  const currentUrl = ref(displayImageUrl.value);
  let lastBaseImageUrl = baseImageUrl.value;

  // Track current preload to cancel if URL changes during load
  let currentPreload = null;

  function cancelPreload() {
    if (currentPreload) {
      currentPreload.onload = null;
      currentPreload.onerror = null;
      currentPreload.src = ''; // Cancel the request
      currentPreload = null;
    }
  }

  watch([displayImageUrl, baseImageUrl], ([newUrl, newBaseUrl]) => {
    if (!newUrl) return;

    // If base image changed (different image entirely), update immediately
    // This prevents showing old image with new image's dimensions
    if (newBaseUrl !== lastBaseImageUrl) {
      lastBaseImageUrl = newBaseUrl;
      cancelPreload();
      currentUrl.value = newUrl;
      return;
    }

    // Same base image, just different resolution - preload before switching
    if (newUrl === currentUrl.value) return;

    cancelPreload();

    const img = new Image();
    currentPreload = img;

    img.onload = () => {
      if (currentPreload === img) {
        currentUrl.value = newUrl;
        currentPreload = null;
      }
    };

    img.src = newUrl;
  });

  onBeforeUnmount(() => {
    cancelPreload();
  });

  const roundingInPixels = computed(() => surfaceEditorUtils.getRoundingInPixels({
    rounding: image.value.rounding,
    elementWidth: props.element.width,
    elementHeight: props.element.height
  }));

  const containerStyle = computed(() => ({
    borderRadius: `${roundingInPixels.value}px`
  }));

  const imageContainerStyle = computed(() => ({
    width: `${image.value.image_width * image.value.scale}px`,
    height: `${image.value.image_height * image.value.scale}px`,
    transform: `translate(${image.value.offset_x}px, ${image.value.offset_y}px)`
  }));
</script>
